import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { SubMenuOption } from 'rangeConfig/@types';
import ListView, { ListViewItem } from '@ingka/list-view';
import styled from 'styled-components';
import { useCore } from 'contexts/core';
import { useSelector } from 'react-redux';
import { getMarketProducts, getSelectedGroupId } from 'ducks/products';
import { useBehaviorTracker } from 'contexts/behaviors';
import Title from 'ui/Typography/Title';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { getMenuTitle } from './utils/utils';
import { useAppDispatch } from 'ducks/hooks';
import { hideProductMenuHeader, showProductMenuHeader } from 'ducks/layout';
import { MenuTitle } from './menuComponents';
import RtlStyles from 'i18n/rtl';
import BackButton from '../BackButton/BackButton';
import Box from 'ui/Box';

type Props = {
  options: SubMenuOption[];
};

const Wrapper = styled.div`
  margin: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.xxl} 0;
`;

const WidgetWrapper = styled(Box)`
  border: 0;
  margin: ${({ theme }) => theme.space.l} 0;
  padding: 0;
`;

export default function ProductsSubMenuList({ options }: Props) {
  const { formatMessage } = useIntl();
  const [selectedSubMenu, setSelectedSubMenu] = useState<SubMenuOption | null>(null);
  const coreApi = useCore();
  const behaviorTracker = useBehaviorTracker();
  const products = useSelector(getMarketProducts);
  const dispatch = useAppDispatch();
  const selectedGroupId = useSelector(getSelectedGroupId);

  const onBackClicked = () => {
    behaviorTracker
      .withContext({ subMenuId: selectedSubMenu?.id, groupId: selectedGroupId, menuType: 'main' })
      .trigger('subMenuExited', null, selectedSubMenu?.behaviors);
    setSelectedSubMenu(null);
    dispatch(showProductMenuHeader());
  };

  const onSelect = (option: SubMenuOption) => {
    setSelectedSubMenu(option);
    dispatch(hideProductMenuHeader());
    behaviorTracker
      .withContext({ subMenuId: option.id, groupId: selectedGroupId, menuType: 'main' })
      .trigger('subMenuEntered', null, option.behaviors);
  };

  return (
    <>
      {selectedSubMenu && (
        <MenuTitle pb="xs">
          <RtlStyles>
            <BackButton
              mr="xs"
              onClick={onBackClicked}
              dataTestId="back-to-main-menu-back-button"
              aria-label={formatMessage({ id: 'storageone.back' })}
            />
          </RtlStyles>

          <RtlStyles>
            <Title type={ThemeFontStyleTypeEnum.headingMedium}>
              {formatMessage({ id: selectedSubMenu.title })}
            </Title>
          </RtlStyles>
        </MenuTitle>
      )}
      <Wrapper>
        {!selectedSubMenu && (
          <ListView id={`sub-menu-list}`} size="large">
            {options.map(option => (
              <ListViewItem
                image={option.image}
                emphasised={true}
                control="navigational"
                controlIcon="chevronRightSmall"
                onClick={() => onSelect(option)}
                key={option.id}
                id={option.id}
                title={formatMessage({ id: option.title })}
              />
            ))}
          </ListView>
        )}
        {selectedSubMenu &&
          selectedSubMenu.widgets
            .filter(w => (w.hidden ? !w.hidden(coreApi, products) : true))
            .map(({ id, component: Component, title }) => (
              <WidgetWrapper data-testid={'widget-wrapper-' + id} key={id}>
                {title && (
                  <Title type={ThemeFontStyleTypeEnum.headingSmall}>
                    {getMenuTitle(title, undefined, formatMessage, coreApi)}
                  </Title>
                )}
                <Component key={id} />
              </WidgetWrapper>
            ))}
      </Wrapper>
    </>
  );
}
